import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import TcoatProductRequestsTable from '../commercialRequests/TcoatProductRequestsTable';
import TcoatProductRequestItemsTable from '../commercialRequests/TcoatProductRequestItemsTable';
import RequestListViewTabs from '../commercialRequests/RequestListViewTabs';
import { TcoatProductRequestForm, emptyTcoatProductRequestForm } from '../forms/TcoatProductRequestForm';
import TcoatProductRequestDetailModal from './TcoatProductRequestDetailModal';
import productionProductRequestsAPI from '../../services/productionProductRequestsAPI';
import { useUIColors } from '../../hooks/useTheme';
import {
  notifyProductionProductRequestsChanged,
  PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT,
} from './commercialRequestsUtils';
import { REQUEST_STATUS } from './planningDomain';

const TcoatProductRequestsPanel = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [rows, setRows] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemsError, setItemsError] = useState('');
  const [listTab, setListTab] = useState('requests');
  const [detailRequest, setDetailRequest] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyTcoatProductRequestForm);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await productionProductRequestsAPI.getAll();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudieron cargar las solicitudes T-COAT.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    setItemsError('');
    try {
      const data = await productionProductRequestsAPI.getAllItems();
      setItemRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setItemsError(err?.message || 'No se pudieron cargar los ítems T-COAT.');
      setItemRows([]);
    } finally {
      setItemsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadRequests(), loadItems()]);
  }, [loadRequests, loadItems]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    const refresh = () => refreshAll();
    window.addEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PRODUCTION_PRODUCT_REQUESTS_CHANGED_EVENT, refresh);
  }, [refreshAll]);

  const listTabs = useMemo(
    () => [
      { key: 'requests', label: 'Solicitudes', count: rows.length },
      { key: 'items', label: 'Ítems', count: itemRows.length },
    ],
    [rows.length, itemRows.length]
  );

  const listSubtitle = loading
    ? 'Cargando…'
    : listTab === 'items'
      ? itemsError || `${itemRows.length} ítems · producción de pintura`
      : error || `${rows.length} solicitud${rows.length === 1 ? '' : 'es'} · producción de pintura`;

  const openCreate = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormError('');
    setFormData(emptyTcoatProductRequestForm());
    setShowForm(true);
  };

  const openEdit = async (request) => {
    const source = request?.items ? request : await productionProductRequestsAPI.getById(request.id);
    setDetailRequest(null);
    setIsEditing(true);
    setEditingId(source.id);
    setFormError('');
    setFormData({
      request_code: source.request_code || '',
      origin: source.origin || 'Comercial',
      reference: source.reference || '',
      needed_by_date: source.needed_by_date ? String(source.needed_by_date).slice(0, 10) : '',
      status: source.status || REQUEST_STATUS.requested,
      quotation_code: source.quotation_code || '',
      sale_code: source.sale_code || '',
      notes: source.notes || '',
      items:
        Array.isArray(source.items) && source.items.length
          ? source.items.map((item) => ({
              product_code: item.product_code || '',
              product_name: item.product_name || '',
              color_code: item.color_code || '',
              color_name: item.color_name || '',
              formula_kind: item.formula_kind || 'color',
              quantity: item.quantity ?? 1,
              unit: item.unit || 'L',
            }))
          : emptyTcoatProductRequestForm().items,
    });
    setShowForm(true);
  };

  const openDetail = async (row) => {
    if (!row?.id) return;
    try {
      const full = await productionProductRequestsAPI.getById(row.id);
      setDetailRequest(full);
    } catch (err) {
      console.error(err);
      setDetailRequest(row);
    }
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const reference = String(formData.reference || '').trim();
    if (!reference) {
      setFormError('La referencia es obligatoria.');
      return;
    }
    const items = (formData.items || [])
      .map((item) => ({
        product_code: String(item.product_code || '').trim(),
        product_name: String(item.product_name || '').trim(),
        color_code: String(item.color_code || '').trim(),
        color_name: String(item.color_name || '').trim(),
        formula_kind: String(item.formula_kind || 'color').trim() || 'color',
        quantity: Number(item.quantity || 0),
        unit: String(item.unit || 'L').trim() || 'L',
      }))
      .filter((item) => item.product_code || item.product_name);
    if (!items.length) {
      setFormError('Agrega al menos un producto con código o nombre.');
      return;
    }

    const payload = {
      origin: String(formData.origin || 'Comercial').trim() || 'Comercial',
      reference,
      needed_by_date: formData.needed_by_date || null,
      status: String(formData.status || '').trim() || REQUEST_STATUS.requested,
      quotation_code: String(formData.quotation_code || '').trim(),
      sale_code: String(formData.sale_code || '').trim(),
      source: isEditing ? '' : 'commercial_manual',
      notes: String(formData.notes || '').trim(),
      items,
    };

    setIsSaving(true);
    setFormError('');
    try {
      if (isEditing && editingId) {
        await productionProductRequestsAPI.update(editingId, payload);
      } else {
        await productionProductRequestsAPI.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(emptyTcoatProductRequestForm());
      await refreshAll();
      notifyProductionProductRequestsChanged();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar la solicitud.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids?.length) return false;
    const ok = window.confirm(
      ids.length === 1 ? '¿Eliminar esta solicitud T-COAT?' : `¿Eliminar ${ids.length} solicitudes T-COAT?`
    );
    if (!ok) return false;
    await productionProductRequestsAPI.bulkDelete(ids);
    await refreshAll();
    notifyProductionProductRequestsChanged();
    return true;
  };

  return (
    <>
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
              Solicitudes de productos T-COAT
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nueva solicitud
          </button>
        </div>

        <RequestListViewTabs
          tabs={listTabs}
          activeKey={listTab}
          onChange={setListTab}
          isDarkMode={isDarkMode}
          ariaLabel="Vistas de solicitudes T-COAT"
        />

        {listTab === 'items' ? (
          <TcoatProductRequestItemsTable
            rows={itemRows}
            loading={itemsLoading}
            error={itemsError}
            isDarkMode={isDarkMode}
            hideChrome
            onRequestClick={openDetail}
          />
        ) : (
          <TcoatProductRequestsTable
            rows={rows}
            loading={loading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            attached
            onRowClick={openDetail}
            onBulkDelete={handleBulkDelete}
          />
        )}
      </div>

      <TcoatProductRequestForm
        isOpen={showForm}
        onClose={() => !isSaving && setShowForm(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={isEditing}
        formData={formData}
        onChange={handleChange}
        formError={formError}
      />

      <TcoatProductRequestDetailModal
        isOpen={Boolean(detailRequest)}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
        isDarkMode={isDarkMode}
        onEdit={openEdit}
      />
    </>
  );
};

export default TcoatProductRequestsPanel;
